import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "acc-page" }
const _hoisted_2 = { class: "acc-card" }
const _hoisted_3 = { class: "acc-timer" }
const _hoisted_4 = { class: "acc-timer-value" }
const _hoisted_5 = { class: "acc-progress" }
const _hoisted_6 = { class: "acc-title" }
const _hoisted_7 = ["aria-label"]
const _hoisted_8 = { class: "acc-banner" }
const _hoisted_9 = { class: "acc-section-title" }
const _hoisted_10 = { class: "acc-steps" }
const _hoisted_11 = {
  key: 0,
  class: "acc-section-title acc-group"
}
const _hoisted_12 = { class: "acc-field" }
const _hoisted_13 = { class: "acc-field-label" }
const _hoisted_14 = { class: "acc-field-value" }
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "acc-actions" }
const _hoisted_17 = ["disabled"]

import "@/assets/css/alfa-c2c-cross-border.css"
import {computed, onMounted, onUnmounted, ref} from "vue"
import {useI18n} from "vue-i18n"
import DealDTO from "@/models/DealDTO"
import InvoiceStateDTO from "@/models/InvoiceStateDTO"
import {copy} from "@/service/helper"
import {numberFormatter} from "@/service/NumberFormatter"


export default /*@__PURE__*/_defineComponent({
  __name: 'AlfaC2cCrossBorderPage',
  props: {
    confirmTransfer: { type: Function },
    cancelDeal: { type: Function },
    currentDeal: {},
    invoice: {}
  },
  setup(__props: any) {

const props = __props

const {t, te} = useI18n({useScope: 'global'})

const now = ref(Date.now())
const copiedKey = ref('')
const confirmPending = ref(false)
let timer: number | null = null

onMounted(() => {
  timer = window.setInterval(() => {
    now.value = Date.now()
  }, 1000)
})

onUnmounted(() => {
  if (timer !== null) {
    clearInterval(timer)
  }
})

const deadline = computed(() => new Date(props.currentDeal.canceledAt).getTime())
const secondsLeft = computed(() => Math.max(0, Math.floor((deadline.value - now.value) / 1000)))
const timerExpired = computed(() => secondsLeft.value <= 0)

const timeLeft = computed(() => {
  const hours = Math.floor(secondsLeft.value / 3600)
  const minutes = Math.floor((secondsLeft.value % 3600) / 60)
  const seconds = secondsLeft.value % 60
  const clock = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  return hours > 0 ? `${hours}:${clock}` : clock
})

const timePercent = computed(() => {
  const createdAt = new Date(props.currentDeal.createdAt).getTime()
  const total = deadline.value - createdAt
  if (total <= 0) {
    return 0
  }
  return Math.min(100, Math.max(0, (deadline.value - now.value) / total * 100))
})

const country = computed(() => {
  const currency = props.invoice.crossBorderCurrency
  if (currency && te(`countries.${currency}`)) {
    return t(`countries.${currency}`)
  }
  return t('alfa-c2c-cross-border.country-fallback')
})

const holderParts = computed(() => {
  const parts = String(props.currentDeal.requisites?.holder ?? '').trim().split(/\s+/).filter((part) => part !== '')
  return {
    surname: parts[0] ?? '',
    name: parts.slice(1).join(' '),
  }
})

const amountText = computed(() => {
  const amount = Number(props.invoice.sum.amount)
  const currency = props.invoice.sum.currency
  if (Number.isFinite(amount) === false || currency === '') {
    return ''
  }
  return numberFormatter.formatMoney(amount, currency) ?? ''
})

const fields = computed(() => {
  const rows: {key: string, heading: string, label: string, value: string, copy: string}[] = []
  const cardNumber = String(props.currentDeal.requisites?.requisites ?? '').trim()
  if (cardNumber !== '') {
    rows.push({
      key: 'card',
      heading: '',
      label: t('alfa-c2c-cross-border.field-card'),
      value: cardNumber,
      copy: cardNumber.replace(/\s/g, ''),
    })
  }
  if (holderParts.value.surname !== '') {
    rows.push({
      key: 'surname',
      heading: t('alfa-c2c-cross-border.recipient'),
      label: t('alfa-c2c-cross-border.field-surname'),
      value: holderParts.value.surname,
      copy: holderParts.value.surname,
    })
  }
  if (holderParts.value.name !== '') {
    rows.push({
      key: 'name',
      heading: holderParts.value.surname === '' ? t('alfa-c2c-cross-border.recipient') : '',
      label: t('alfa-c2c-cross-border.field-name'),
      value: holderParts.value.name,
      copy: holderParts.value.name,
    })
  }
  if (amountText.value !== '') {
    rows.push({
      key: 'amount',
      heading: t('alfa-c2c-cross-border.amount'),
      label: t('alfa-c2c-cross-border.field-amount'),
      value: amountText.value,
      copy: amountText.value,
    })
  }
  return rows
})

const instructionSteps = computed(() => [
  t('alfa-c2c-cross-border.instruction.step-1'),
  t('alfa-c2c-cross-border.instruction.step-2'),
  t('alfa-c2c-cross-border.instruction.step-3', {country: country.value}),
  t('alfa-c2c-cross-border.instruction.step-4'),
  t('alfa-c2c-cross-border.instruction.step-5'),
  t('alfa-c2c-cross-border.instruction.step-6'),
])

const copyField = (key: string, value: string) => {
  copy(null, value)
  copiedKey.value = key
  window.setTimeout(() => {
    if (copiedKey.value === key) {
      copiedKey.value = ''
    }
  }, 1000)
}

const onConfirm = () => {
  confirmPending.value = true
  props.confirmTransfer()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("article", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("span", null, _toDisplayString(_ctx.$t('alfa-c2c-cross-border.time-left')), 1),
        _createElementVNode("span", _hoisted_4, _toDisplayString(timeLeft.value), 1)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("span", {
          style: _normalizeStyle({width: timePercent.value + '%'})
        }, null, 4)
      ]),
      _createElementVNode("h1", _hoisted_6, [
        _createTextVNode(_toDisplayString(_ctx.$t('alfa-c2c-cross-border.title', {country: country.value})) + " ", 1),
        (_ctx.invoice.crossBorderCurrency === 'TJS')
          ? (_openBlock(), _createElementBlock("svg", {
              key: 0,
              class: "acc-flag",
              viewBox: "0 0 28 16",
              role: "img",
              "aria-label": country.value
            }, _cache[1] || (_cache[1] = [
              _createElementVNode("path", {
                fill: "#cc2028",
                d: "M0 0h28v5H0z"
              }, null, -1),
              _createElementVNode("path", {
                fill: "#fff",
                d: "M0 5h28v6H0z"
              }, null, -1),
              _createElementVNode("path", {
                fill: "#168447",
                d: "M0 11h28v5H0z"
              }, null, -1)
            ]), 8, _hoisted_7))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t('alfa-c2c-cross-border.banner')), 1),
      _createElementVNode("h2", _hoisted_9, _toDisplayString(_ctx.$t('alfa-c2c-cross-border.instruction-title')), 1),
      _createElementVNode("ol", _hoisted_10, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(instructionSteps.value, (step) => {
          return (_openBlock(), _createElementBlock("li", { key: step }, _toDisplayString(step), 1))
        }), 128))
      ]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fields.value, (row) => {
        return (_openBlock(), _createElementBlock(_Fragment, {
          key: row.key
        }, [
          (row.heading)
            ? (_openBlock(), _createElementBlock("h2", _hoisted_11, _toDisplayString(row.heading), 1))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("span", _hoisted_13, _toDisplayString(row.label), 1),
            _createElementVNode("span", _hoisted_14, _toDisplayString(row.value), 1),
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass(["acc-copy", {copied: copiedKey.value === row.key}]),
              onClick: ($event: any) => (copyField(row.key, row.copy))
            }, _toDisplayString(copiedKey.value === row.key ? _ctx.$t('alfa-c2c-cross-border.copied') : _ctx.$t('alfa-c2c-cross-border.copy')), 11, _hoisted_15)
          ])
        ], 64))
      }), 128)),
      _createElementVNode("div", _hoisted_16, [
        _createElementVNode("button", {
          type: "button",
          class: "acc-confirm",
          disabled: confirmPending.value || timerExpired.value,
          onClick: onConfirm
        }, _toDisplayString(confirmPending.value ? _ctx.$t('alfa-c2c-cross-border.confirming') : _ctx.$t('alfa-c2c-cross-border.confirm')), 9, _hoisted_17),
        _createElementVNode("button", {
          type: "button",
          class: "acc-cancel",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.cancelDeal(_ctx.invoice.cancelUrl)))
        }, _toDisplayString(_ctx.invoice.cancelUrl ? _ctx.$t('alfa-c2c-cross-border.back-to-shop') : _ctx.$t('alfa-c2c-cross-border.cancel')), 1)
      ])
    ])
  ]))
}
}

})